import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getTheme, useThemeMode, RADIUS, TYPE } from '../theme';
import { api, resolveUrl, qmeetApi, bearerHeaders, AD_KIND_LABEL, type MarkAd, QidUser, HuntListing, Booking, Mark, MarkCalendarEvent, MarkApplication, JaktklubAd, JaktklubApplication } from '../api';
import type {
  View, CartItem,
  AiResult, ChatMsg, TranscriptEntry, HuntObsRow, HuntKillRow,
  MdTab, WizardData,
  MeatProduct,
} from '../types';
import {
  Loader, Empty,
  HuntCard,
  MarkCard, markImg,
  MeatCard,
  CartSidebar,
  GREEN_PLACEHOLDER, fmtD,
} from '../components';
import { NAV_MAIN_H, NAV_TABS_H, NAV_SEARCH_SLOT_ID } from '../components/TopNav';
import { AreaPickerModal, AreaFilterChip, inArea, type PickedArea } from '../geo/AreaFilter';
import {
  EditListingModal, EditMarkModal,
  MeatFormModal, CompletedHuntModal, AppContractFlow,
  HuntMap, MeatMap, HeroSearch, MarkRegistrationWizard, DetailModal,
} from '../modals';
import ArrendeChat from '../ArrendeChat';
import { planSpeciesFamilyKey } from '../swedishHuntingLegality';
import { GL, GT } from './shared';
import { MyMarkDetailView } from '../App';
import { ShowDemoMarkButton, isDemoMark } from './minjakt/DemoMark';
import { OPEN_MARK_TAB_EVENT, OPEN_HUNT_CHAT_EVENT } from '../push';
import { MyPurchases } from './MyPurchases';
import { MyLegacyApplications } from '../mark-tabs/LegacyApplications';
import { JaktHeader } from './jakt/MarkList';
import { DEFAULT_JAKT_SUB_TAB, marketplaceAds, type JaktSubTab } from './jakt/marketplace';
import { MarkPage } from './jakt/MarkPage';
import { AdList, adPath, adIdFromPath } from './jakt/AdList';
import { markPath, markSlug, slugFromPath, loadFavs, saveFavs } from './jakt/markData';
import { useIsMobile } from '../hooks/useIsMobile';
import { ChevronLeft, ChevronRight, Crosshair, MapPin } from 'lucide-react';
import { Badge, Button, Card, EmptyState, Icon, IconButton, SectionHeader, type BadgeTone } from '../components/ui';
import { EventRow, EventTypeBadge, TextLink, timeOf } from './minjakt/parts';
import { MarkListRow, nextHunt, useMarkListExtras } from './minjakt/MarkList'; // MJ1
import { AfterHuntReminder } from '../components/AfterHuntReminder'; // P4
import { notifyHuntEconomyChanged } from '../huntEconomy'; // EJ5: Efter jakt laddas om när rutan sparat fällt vilt

const MEAT_PRODUCTS: MeatProduct[] = [
  { id:'m1', species:'Dovhjort', cut:'Innanlår', weightG:800, pricePerKg:300, totalPrice:250, location:'Tyresö', postalCode:'135 52', shotAt:'Lilla Brandsbol', seller:'Erik Lindgren', swish:true, frozen:false, description:'Färskt innanlår av dovhjort, mört och smakfullt. Skjutet i november.' },
  { id:'m2', species:'Älg', cut:'Ytterlår', weightG:2400, pricePerKg:180, totalPrice:432, location:'Borlänge', postalCode:'784 33', shotAt:'Svärdsjö vvo', seller:'Anders Björk', swish:true, frozen:true, description:'Fryst ytterlår från årets älgjakt. Perfekt för gryta eller stek.' },
  { id:'m3', species:'Vildsvin', cut:'Karré', weightG:1200, pricePerKg:220, totalPrice:264, location:'Enköping', postalCode:'745 31', shotAt:'Grillby gård', seller:'Maria Holm', swish:true, frozen:false, description:'Fin vildsvinkarré, perfekt för grillning eller ugn. Mörkrosa, mager.' },
  { id:'m4', species:'Rådjur', cut:'Sadel', weightG:600, pricePerKg:380, totalPrice:228, location:'Sigtuna', postalCode:'193 30', shotAt:'Venngarn', seller:'Karl-Johan Falk', swish:true, frozen:false, description:'Rådjurssadel — en delikatess. Snabbstekt i smör med timjan och vitlök.' },
  { id:'m5', species:'Kronhjort', cut:'Bog', weightG:3200, pricePerKg:150, totalPrice:480, location:'Kristianstad', postalCode:'291 31', shotAt:'Bäckaskog slott', seller:'Nils Persson', swish:true, frozen:true, description:'Stor kronhjortsbog, utmärkt för långkok och pulled game. Fryst.' },
  { id:'m6', species:'Dovhjort', cut:'Fransyska', weightG:950, pricePerKg:260, totalPrice:247, location:'Nyköping', postalCode:'611 31', shotAt:'Arnö vvo', seller:'Lisa Engström', swish:true, frozen:false, description:'Dovhjortsfransyska, perfekt till helgmiddagen. Hängmörad 10 dagar.' },
  { id:'m7', species:'Vildsvin', cut:'Pulled (färdig)', weightG:500, pricePerKg:340, totalPrice:170, location:'Uppsala', postalCode:'753 20', shotAt:'Vattholma', seller:'Jägargården AB', swish:true, frozen:true, description:'Färdiglagad pulled vildsvin med rökig smak. Bara värma och servera.' },
  { id:'m8', species:'Älg', cut:'Färs', weightG:1000, pricePerKg:140, totalPrice:140, location:'Falun', postalCode:'791 71', shotAt:'Bjursås vvo', seller:'Sven Dahl', swish:true, frozen:true, description:'Finmald älgfärs, blandad 80/20 med fläskfett. Perfekt till köttbullar.' },
  { id:'m9', species:'Rådjur', cut:'Entrecôte', weightG:450, pricePerKg:420, totalPrice:189, location:'Lidingö', postalCode:'181 30', shotAt:'Elfvik', seller:'Oskar Welin', swish:true, frozen:false, description:'Exklusiv rådjurs-entrecôte, mör och smakrik. Hängmörad 14 dagar.' },
  { id:'m10', species:'Vildsvin', cut:'Revben', weightG:1800, pricePerKg:160, totalPrice:288, location:'Strängnäs', postalCode:'645 30', shotAt:'Gorsingeholm', seller:'Peter Åman', swish:true, frozen:false, description:'Vildsvinsrevben — slow cook i 6 timmar med BBQ-glaze. Fantastiskt mört.' },
  { id:'m11', species:'Fasan', cut:'Hel fågel', weightG:700, pricePerKg:280, totalPrice:196, location:'Ystad', postalCode:'271 00', shotAt:'Marsvinsholm', seller:'Gustaf von Essen', swish:true, frozen:false, description:'Hel fasan, plocklad och klar. Stek i ugn med rotsaker.' },
  { id:'m12', species:'Älg', cut:'Filé', weightG:600, pricePerKg:450, totalPrice:270, location:'Mora', postalCode:'792 30', shotAt:'Sollerön vvo', seller:'Gunnar Matsson', swish:true, frozen:false, description:'Älgfilé av finaste kvalitet. Snabbstekt med svampsås — middag på 20 min.' },
];

const MEAT_SPECIES = ['Alla','Dovhjort','Älg','Vildsvin','Rådjur','Kronhjort','Fasan'];

// ── HUNT FILTER CONSTANTS ─────────────────────────────────────────
const HUNT_TYPES = ['Alla','Drevjakt','Smygjakt','Fågeljakt','Jaktlag','Jaktklubb','Gästjakt','Hundförare'];
const HUNT_SPECIES = ['Alla','Dovhjort','Älg','Vildsvin','Rådjur','Kronhjort','Fasan'];
// ── MARKETPLACE TOP BAR ──────────────────────────────────────────


// ── HERO SEARCH / AI CHAT (extracted to src/modals/HeroSearch.tsx) ──


// ── PARSE HUNT TRANSCRIPT ─────────────────────────────────────────

/** Ordgränsmatchning som hanterar svenska tecken (undviker t.ex. "strand" → "and"). */
export function kwInLine(ll: string, kw: string): boolean {
  return new RegExp(`(?:^|[\\s,;.!?\\-—/])${kw}(?=$|[\\s,;.!?\\-—/])`, 'i').test(ll);
}

export function parseHuntTranscript(transcription: string): {antalDrev: number; observations: string[]; kills: string[]} {
  if (!transcription) return {antalDrev: 0, observations: [], kills: []};
  const text = transcription.toLowerCase();
  const numberedDrev = new Set<string>();
  for (const m of text.matchAll(/\bdrev\s+(?:\d+|ett|två|tre|fyra|fem|sex|sju|åtta|nio|tio)\b/g)) numberedDrev.add(m[0].trim());
  let newDrevCount = 0;
  for (const _m of text.matchAll(/\bnytt\s+drev\b|\bkör\s+igång\b|\bsätt\s+igång\b|\bstart\s+(?:av\s+)?drev\b|\bdrev\s+start\b/g)) newDrevCount++;
  let antalDrev = Math.max(numberedDrev.size, newDrevCount);
  if (antalDrev === 0 && text.includes('drev')) antalDrev = 1;
  const gameAnimals = [
    {sv: 'Älg', kw: ['älg','älgko','älgtjur','älgkalv','kviga']},
    {sv: 'Dovhjort', kw: ['dovhjort','dovko','dovtjur','dovkalv','dovhind','dovspets']},
    {sv: 'Kronhjort', kw: ['kronhjort','kronko','krondj','kronkalv','kapitalhjort']},
    {sv: 'Rådjur', kw: ['rådjur','råbock','råget','råkid','rådjursget']},
    {sv: 'Vildsvin', kw: ['vildsvin','galt','sugga','smågris','kolting','årsgris']},
    {sv: 'Mufflon', kw: ['mufflon','bagge']},
    {sv: 'Björn', kw: ['björn']},
    {sv: 'Hare', kw: ['hare']},
    {sv: 'Fasan', kw: ['fasan']},
    {sv: 'Orre', kw: ['orre']},
    {sv: 'Tjäder', kw: ['tjäder']},
    {sv: 'Andfågel', kw: ['andfågel','gräsand','kricka','bläsand']},
    {sv: 'Kanadagås', kw: ['kanadagås','grågås','gäss']},
    {sv: 'Räv', kw: ['räv']},
    {sv: 'Grävling', kw: ['grävling']},
    {sv: 'Bäver', kw: ['bäver']},
    {sv: 'Mink', kw: ['mink']},
    {sv: 'Mårdhund', kw: ['mårdhund']},
  ];
  const killPat = /\b(sköt|skjutit|skjuten|skjutit på|påskjut|påskjuten|skott|fällt|fälld|träffad|nedlagd|nedlagt|knäckt|tog ner|prick|skutt|lagt ned|ligger|liggande|är nere|var nere|döende|dött|dog)\b/;
  const observations: string[] = [], kills: string[] = [];
  const lastKillLine: Record<string,number> = {};
  const lines = transcription.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const ll = lines[i].toLowerCase();
    const isKill = killPat.test(ll);
    for (const g of gameAnimals) {
      if (g.kw.some(k => kwInLine(ll, k))) {
        if (isKill) {
          if (!kills.includes(g.sv)) kills.push(g.sv);
          lastKillLine[g.sv] = i;
        } else {
          const lastKill = lastKillLine[g.sv];
          if (lastKill != null && i - lastKill <= 3) continue;
          if (!kills.includes(g.sv) && !observations.includes(g.sv)) observations.push(g.sv);
        }
      }
    }
  }
  return {antalDrev, observations, kills};
}

export function parseTranscriptEntries(entries: TranscriptEntry[]): {antalDrev: number; obsRows: HuntObsRow[]; killRows: HuntKillRow[]} {
  if (!entries || entries.length === 0) return {antalDrev: 0, obsRows: [], killRows: []};
  const gameAnimals = [
    {sv: 'Älg', kw: ['älg','älgko','älgtjur','älgkalv','kviga']},
    {sv: 'Dovhjort', kw: ['dovhjort','dovko','dovtjur','dovkalv','dovhind','dovspets']},
    {sv: 'Kronhjort', kw: ['kronhjort','kronko','krondj','kronkalv','kapitalhjort']},
    {sv: 'Rådjur', kw: ['rådjur','råbock','råget','råkid','rådjursget']},
    {sv: 'Vildsvin', kw: ['vildsvin','galt','sugga','smågris','kolting','årsgris']},
    {sv: 'Mufflon', kw: ['mufflon','bagge']},
    {sv: 'Björn', kw: ['björn']},
    {sv: 'Hare', kw: ['hare']},
    {sv: 'Fasan', kw: ['fasan']},
    {sv: 'Orre', kw: ['orre']},
    {sv: 'Tjäder', kw: ['tjäder']},
    {sv: 'Andfågel', kw: ['andfågel','gräsand','kricka','bläsand']},
    {sv: 'Kanadagås', kw: ['kanadagås','grågås','gäss']},
    {sv: 'Räv', kw: ['räv']},
    {sv: 'Grävling', kw: ['grävling']},
    {sv: 'Bäver', kw: ['bäver']},
    {sv: 'Mink', kw: ['mink']},
    {sv: 'Mårdhund', kw: ['mårdhund']},
  ];
  const killPat = /\b(sköt|skjutit|skjuten|skjutit på|påskjut|påskjuten|skott|fällt|fälld|träffad|nedlagd|nedlagt|knäckt|tog ner|prick|skutt|lagt ned|ligger|liggande|är nere|var nere|döende|dött|dog)\b/;
  const fullText = entries.map(e => e.text).join('\n').toLowerCase();
  const numberedDrev = new Set<string>();
  for (const m of fullText.matchAll(/\bdrev\s+(?:\d+|ett|två|tre|fyra|fem|sex|sju|åtta|nio|tio)\b/g)) numberedDrev.add(m[0].trim());
  let newDrevCount = 0;
  for (const _m of fullText.matchAll(/\bnytt\s+drev\b|\bkör\s+igång\b|\bsätt\s+igång\b|\bstart\s+(?:av\s+)?drev\b|\bdrev\s+start\b/g)) newDrevCount++;
  let antalDrev = Math.max(numberedDrev.size, newDrevCount);
  if (antalDrev === 0 && fullText.includes('drev')) antalDrev = 1;
  const obsRows: HuntObsRow[] = [];
  const killRows: HuntKillRow[] = [];
  const lastKillTs = new Map<string, number>();
  const SUPPRESS_MS = 120_000;
  for (const entry of entries) {
    const ll = entry.text.toLowerCase();
    const isKill = killPat.test(ll);
    for (const g of gameAnimals) {
      if (g.kw.some(k => kwInLine(ll, k))) {
        if (isKill) {
          lastKillTs.set(g.sv, entry.ts);
          let kon = 'Okänt';
          if (/\b(tjur|hane|galt|bock)\b/.test(ll)) kon = 'Hane';
          else if (/\b(ko|hona|sugga|get|hind)\b/.test(ll)) kon = 'Hona';
          else if (/\b(kalv|kultingen?|kid|unge)\b/.test(ll)) kon = 'Kalv';
          killRows.push({art: g.sv, speaker: entry.speaker, lat: entry.lat, lng: entry.lng, ts: entry.ts, count: 1, kon});
        } else {
          const prevKill = lastKillTs.get(g.sv);
          if (prevKill != null && entry.ts - prevKill < SUPPRESS_MS) continue;
          obsRows.push({art: g.sv, speaker: entry.speaker, lat: entry.lat, lng: entry.lng, ts: entry.ts, count: 1});
        }
      }
    }
  }
  return {antalDrev, obsRows, killRows};
}


// ── VIEW: MARKETPLACE ────────────────────────────────────────────

// Viltkött säljs och köps på Qcarne
export const QCARNE_URL='https://qcarne.com';

export function VMarketplace({af,onF,cart,setCart,user,isAdmin,onMer,mpTab,setMpTab,onNav,onLogin,joinMeetingId,joinMarkId,joinPendingMark,onJoinPendingMarkConsumed,huntCartOpen,onSetHuntCartOpen,userAvatarUrl,onMarkOpen,navTick}:{af:string;onF:(f:string)=>void;cart:CartItem[];setCart:React.Dispatch<React.SetStateAction<CartItem[]>>;user?:QidUser|null;isAdmin?:boolean;onMer?:()=>void;mpTab:'minajakt'|'jakt'|'arrende';setMpTab:(t:'minajakt'|'jakt'|'arrende')=>void;onNav?:(v:View)=>void;onLogin?:()=>void;joinMeetingId?:string|null;joinMarkId?:string|null;joinPendingMark?:Mark|null;onJoinPendingMarkConsumed?:()=>void;huntCartOpen:boolean;onSetHuntCartOpen:(v:boolean)=>void;userAvatarUrl?:string|null;onMarkOpen?:(open:boolean)=>void;navTick?:number}){
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const iS:React.CSSProperties={width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:4,padding:'10px 14px',color:T.text,fontSize:14,outline:'none',boxSizing:'border-box',fontFamily:'inherit'};
  const cS:React.CSSProperties={background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:20};
  const secT:React.CSSProperties={color:T.textMuted,fontSize:11,fontWeight:600,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:16};
  const[ls,setLs]=useState<HuntListing[]>([]);const[ld,setLd]=useState(true);
  const[sel,setSel]=useState<HuntListing|null>(null);
  const[editListing,setEditListing]=useState<HuntListing|null>(null);
  const[aiRes,setAiRes]=useState<AiResult|null>(null);
  const[marks,setMarks]=useState<Mark[]>([]);
  const[selMark,setSelMark]=useState<Mark|null>(null);
  /** Annonsen som visas på /annons/:id. */
  const[selAd,setSelAd]=useState<MarkAd|null>(null);
  /** Öppna en egen mark på en viss flik (t.ex. Annonser eller Ekonomi från en annons). */
  const[myMarkOpenAt,setMyMarkOpenAt]=useState<{tab:MdTab;groupId?:string|null;adId?:string|null;mapFullscreen?:boolean;seq?:number}|null>(null);
  const[adsReload,setAdsReload]=useState(0);const[editMark,setEditMark]=useState<Mark|null>(null);
  const[showMarkWizard,setShowMarkWizard]=useState(false);
  const[wizardInitData,setWizardInitData]=useState<Partial<WizardData>|undefined>(undefined);
  const[huntTypeFilter,setHuntTypeFilter]=useState('Alla');
  const[huntSpeciesFilter,setHuntSpeciesFilter]=useState('Alla');
  const[huntShowMap,setHuntShowMap]=useState(false);
  const[meatFilter,setMeatFilter]=useState('Alla');
  const[meatShowMap,setMeatShowMap]=useState(false);
  const[selMeat,setSelMeat]=useState<MeatProduct|null>(null);
  const[allMeat,setAllMeat]=useState<MeatProduct[]>([]);
  const filteredMeat=meatFilter==='Alla'?allMeat:allMeat.filter(m=>m.species===meatFilter);
  useEffect(()=>{api.meatProducts().then(d=>setAllMeat(d as unknown as MeatProduct[])).catch(()=>setAllMeat(MEAT_PRODUCTS as any));},[]);
  const[showAddMeat,setShowAddMeat]=useState(false);
  const[editMeatProduct,setEditMeatProduct]=useState<MeatProduct|null>(null);
  const[myMarks2,setMyMarks2]=useState<Mark[]>([]);
  /** UX4: markerna man kan starta/planera jakt på från kartans jaktknapp (inte demomarken). */
  const huntMarks=React.useMemo(()=>myMarks2.filter(m=>!isDemoMark(m)).map(m=>({id:m.id,name:m.name})),[myMarks2]);
  /** MJ1: olästa och senaste bilder per mark i listan på Min jakt. */
  const markListExtras=useMarkListExtras(user&&mpTab==='minajakt'?myMarks2:[]);
  const[myBookings,setMyBookings]=useState<Booking[]>([]);
  const[showAllJakter,setShowAllJakter]=useState(false);
  const[jakterPage,setJakterPage]=useState(1);
  const[myApps,setMyApps]=useState<MarkApplication[]>([]);
  const[appContractModal,setAppContractModal]=useState<{app:MarkApplication;mark:Mark|null}|null>(null);
  const[myMarkDetail,setMyMarkDetail]=useState<Mark|null>(null);
  const[jaktSubTab,setJaktSubTab]=useState<JaktSubTab>(DEFAULT_JAKT_SUB_TAB);
  const[jaktklubbarList,setJaktklubbarList]=useState<JaktklubAd[]>([]);const[jaktklubbarLd,setJaktklubbarLd]=useState(false);const[jaktklubbarLoaded,setJaktklubbarLoaded]=useState(false);
  const[selJaktklubb,setSelJaktklubb]=useState<JaktklubAd|null>(null);
  const[selJkMark,setSelJkMark]=useState<Mark|null>(null);const[selJkMarkLd,setSelJkMarkLd]=useState(false);
  const mob=useIsMobile();
  const pageOpen=!!selMark||!!selAd||!!(selJaktklubb&&selJkMark);
  // TopNav döljs bara på mobil, där markens sida och jaktsidan har en egen rad med tillbaka.
  useEffect(()=>{onMarkOpen?.(mob&&(!!myMarkDetail||pageOpen))},[myMarkDetail,mob,pageOpen]); // eslint-disable-line
  const[subMarkIds,setSubMarkIds]=useState<Set<string>>(new Set());
  const[myCalEvents,setMyCalEvents]=useState<MarkCalendarEvent[]>([]);
  const[calViewYear,setCalViewYear]=useState(()=>new Date().getFullYear());
  const[calViewMonth,setCalViewMonth]=useState(()=>new Date().getMonth());
  const[dashCompletedHunt,setDashCompletedHunt]=useState<MarkCalendarEvent|null>(null);
  const[dashUpdateGoals,setDashUpdateGoals]=useState(false);
  const[showAllKommande,setShowAllKommande]=useState(false);
  /** Månadskalendern på Min jakt visas först när man ber om den (Kommande-listan räcker oftast). */
  const[showCal,setShowCal]=useState(false);
  const[showAllGenom,setShowAllGenom]=useState(false);
  useEffect(()=>{
    if(!user)return;
    api.myMarks().then(d=>{setMyMarks2(d);setSubMarkIds(new Set(d.map(m=>m.id)))}).catch(()=>{});
    api.myHuntBookings().then(setMyBookings).catch(()=>{});
    api.myApplications().then(setMyApps).catch(()=>{});
  },[user]);
  useEffect(()=>{
    if(!myMarks2.length)return;
    // MJ1: Min jakt visar listan över markerna; hemmamarken öppnas inte längre av sig själv (den hamnar överst).
    Promise.all(myMarks2.map(m=>api.markCalendarEvents(m.id))).then(all=>{
      setMyCalEvents(all.flat().sort((a,b)=>new Date(a.startTime).getTime()-new Date(b.startTime).getTime()));
    }).catch(()=>{});
  },[myMarks2.length]);
  const[merOpen,setMerOpen]=useState(false);
  /** Admin: markägaravtal (Erbjud jakt) som väntar på admin-signering. */
  const[adminMarkContractsOpen,setAdminMarkContractsOpen]=useState(false);
  const[adminMarkContracts,setAdminMarkContracts]=useState<import('../api').MarkOwnerContract[]>([]);
  const[adminMarkContractsLd,setAdminMarkContractsLd]=useState(false);
  const[adminMarkContractsErr,setAdminMarkContractsErr]=useState('');
  const[signingContractId,setSigningContractId]=useState<string|null>(null);
  const[signNameInput,setSignNameInput]=useState('');
  const loadAdminMarkContracts=async()=>{setAdminMarkContractsLd(true);setAdminMarkContractsErr('');try{const cs=await api.markOwnerContracts();setAdminMarkContracts(cs);}catch(e:any){setAdminMarkContractsErr(e.message||'Fel vid hämtning');}finally{setAdminMarkContractsLd(false);};};
  /** Admin: lista / stäng pågående Qmeet-jaktmöten (Mer → Hantera). */
  const[adminOngoingHuntsOpen,setAdminOngoingHuntsOpen]=useState(false);
  const[adminOngoingMeetings,setAdminOngoingMeetings]=useState<Array<{id:string;title:string;status:string;host_name?:string;created_at?:number}>>([]);
  const[adminOngoingMeetingsLd,setAdminOngoingMeetingsLd]=useState(false);
  const[adminOngoingMeetingsErr,setAdminOngoingMeetingsErr]=useState('');
  const[closingMeetingId,setClosingMeetingId]=useState<string|null>(null);
  const merRef=useRef<HTMLDivElement>(null);
  const tabBarRef=useRef<HTMLDivElement>(null);
  const[tabBarW,setTabBarW]=useState(0);
  const[showPersonalizeNav,setShowPersonalizeNav]=useState(false);
  const[navTabOrder,setNavTabOrder]=useState<string[]>(()=>{try{const s=localStorage.getItem('qvenari_nav_tab_order');return s?JSON.parse(s):[];}catch{return [];}});
  const[markOrderPref,setMarkOrderPref]=useState<string[]>(()=>{try{const s=localStorage.getItem('qvenari_mark_order');return s?JSON.parse(s):[];}catch{return [];}});
  const[homeMarkId,setHomeMarkId]=useState<string|null>(()=>localStorage.getItem('qvenari_home_mark'));
  const[dragNavIdx,setDragNavIdx]=useState<number|null>(null);
  const[dragMarkIdx,setDragMarkIdx]=useState<number|null>(null);
  useEffect(()=>{
    const measure=()=>{const el=tabBarRef.current;if(!el)return;const w=el.getBoundingClientRect().width;if(w>0)setTabBarW(w);};
    measure();
    const el=tabBarRef.current;if(!el)return;
    const ro=new ResizeObserver(e=>{const w=e[0].contentRect.width;if(w>0)setTabBarW(w);});
    ro.observe(el);
    // fallback: re-measure after paint in case width was 0 on first call
    const raf=requestAnimationFrame(measure);
    return()=>{ro.disconnect();cancelAnimationFrame(raf);}
  },[]);
  useEffect(()=>{if(!merOpen)return;const h=(e:MouseEvent)=>{if(merRef.current&&!merRef.current.contains(e.target as Node))setMerOpen(false)};document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h)},[merOpen]);
  const loadAdminOngoingMeetings=useCallback(async()=>{
    setAdminOngoingMeetingsLd(true);setAdminOngoingMeetingsErr('');
    try{
      const r=await fetch(qmeetApi('/api/meetings?limit=150'),{headers:{...bearerHeaders()}});
      if(!r.ok)throw new Error(`Kunde inte hämta möten (HTTP ${r.status})`);
      const all=(await r.json())as Array<{id:string;title:string;status:string;host_name?:string;created_at?:number}>;
      setAdminOngoingMeetings(all.filter(m=>m.status==='active'||m.status==='scheduled'));
    }catch(e:unknown){setAdminOngoingMeetingsErr((e as Error).message||'Nätverksfel');setAdminOngoingMeetings([]);}
    finally{setAdminOngoingMeetingsLd(false);}
  },[]);
  // All marketplace tabs in priority order
  const ALL_MP_TABS_BASE=[
    ...(user?[{k:'minajakt' as const,l:'Min Jakt',w:90}]:[]),
    {k:'jakt' as const,l:'Mer jakt',w:90},{k:'arrende' as const,l:'Erbjud jakt',w:108}
  ];
  const ALL_MP_TABS=navTabOrder.length>0?[...ALL_MP_TABS_BASE].sort((a,b)=>{const ai=navTabOrder.indexOf(a.k);const bi=navTabOrder.indexOf(b.k);return(ai===-1?999:ai)-(bi===-1?999:bi);}):ALL_MP_TABS_BASE;
  // Calc how many tabs fit; if not yet measured (tabBarW=0) show all
  const MER_W=88;
  let usedW=MER_W;let visCnt=0;
  if(tabBarW>0){for(const t of ALL_MP_TABS){const tw=t.w+30;if(usedW+tw<=tabBarW){usedW+=tw;visCnt++;}else break;}}
  const primTabs=tabBarW===0?ALL_MP_TABS:ALL_MP_TABS.slice(0,visCnt);
  const overflowTabs=tabBarW===0?[]:ALL_MP_TABS.slice(visCnt);
  // Consume join pending mark from AppInner (avoids ReferenceError when AppInner useEffect ran)
  useEffect(()=>{
    if(joinPendingMark&&!myMarkDetail){
      setMyMarkDetail(joinPendingMark);
      onJoinPendingMarkConsumed?.();
    }
  },[joinPendingMark]); // eslint-disable-line
  useEffect(()=>{setLd(true);api.huntListings(af==='Alla'?undefined:af).then(d=>{setLs(d);setLd(false)}).catch(()=>setLd(false))},[af]);
  const refreshListings=()=>{api.huntListings(af==='Alla'?undefined:af).then(d=>setLs(d)).catch(()=>{})};
  useEffect(()=>{api.marks(isAdmin?undefined:true).then(d=>{setMarks(d)}).catch(()=>{})},[isAdmin]);

  // ── Jaktsidan som egen route: /jakt/:slug (bakåt i webbläsaren stänger den) ──
  const jaktListLabel='Jaktlag';
  const openMark=useCallback((m:Mark)=>{
    if(window.location.pathname!==markPath(m))window.history.pushState({qvMark:m.id},'',markPath(m));
    setSelMark(m);
  },[]);
  const closeMark=useCallback(()=>{
    if(window.history.state?.qvMark)window.history.back();
    else{window.history.replaceState(null,'','/'+window.location.search);setSelMark(null);}
  },[]);
  const openAd=useCallback((ad:MarkAd)=>{
    if(window.location.pathname!==adPath(ad))window.history.pushState({qvAd:ad.id},'',adPath(ad));
    setSelAd(ad);window.scrollTo({top:0});
  },[]);
  const closeAd=useCallback(()=>{
    if(window.history.state?.qvAd)window.history.back();
    else{window.history.replaceState(null,'','/'+window.location.search);setSelAd(null);}
  },[]);
  useEffect(()=>{
    // Direktlänk eller bakåt/framåt till /annons/:id
    const syncAd=()=>{
      const id=adIdFromPath(window.location.pathname);
      if(!id){setSelAd(null);return;}
      api.markAd(id).then(a=>{if(a){setSelAd(a);setMpTab('jakt');}}).catch(()=>{});
    };
    syncAd();
    window.addEventListener('popstate',syncAd);
    return()=>window.removeEventListener('popstate',syncAd);
  },[]); // eslint-disable-line
  // Tillbaka från Stripe Connect (App.tsx): öppna markens Ekonomi-flik när markerna laddats.
  useEffect(()=>{
    let want:{markId:string;tab:MdTab;mapFullscreen?:boolean}|null=null;
    try{want=JSON.parse(sessionStorage.getItem('qvenari_open_mark_tab')||'null');}catch{}
    if(!want||!myMarks2.some(m=>m.id===want!.markId))return;
    try{sessionStorage.removeItem('qvenari_open_mark_tab');}catch{}
    openMarkTab(want.markId,want.tab,{mapFullscreen:want.mapFullscreen});
  },[myMarks2]); // eslint-disable-line react-hooks/exhaustive-deps
  /** Från en annons till markens egna sidor (Annonser/Ekonomi) — kräver att marken är ens egen. */
  const openMarkTab=(markId:string,tab:MdTab,extra?:{groupId?:string|null;adId?:string|null;mapFullscreen?:boolean},fallback?:Mark|null)=>{
    const m=myMarks2.find(x=>x.id===markId)||marks.find(x=>x.id===markId)||(fallback&&fallback.id===markId?fallback:null);
    if(!m)return;
    if(adIdFromPath(window.location.pathname)||slugFromPath(window.location.pathname))window.history.replaceState(null,'','/'+window.location.search);
    setSelAd(null);setSelMark(null);
    setMyMarkOpenAt({tab,...extra,seq:Date.now()});setMyMarkDetail(m);
  };
  // Tryck på en pushnotis (App.tsx): marken öppnas här om den redan är laddad, annars av
  // effekten ovan när myMarks2 kommer.
  useEffect(()=>{
    const onOpen=(e:Event)=>{
      const {markId,tab,huntChat,mapFullscreen}=(e as CustomEvent<{markId:string;tab:MdTab;huntChat?:boolean;mapFullscreen?:boolean}>).detail;
      if(!myMarks2.some(m=>m.id===markId)&&!marks.some(m=>m.id===markId))return;
      try{sessionStorage.removeItem('qvenari_open_mark_tab');}catch{}
      openMarkTab(markId,tab,{mapFullscreen});
      if(huntChat)setTimeout(()=>window.dispatchEvent(new Event(OPEN_HUNT_CHAT_EVENT)),400);
    };
    window.addEventListener(OPEN_MARK_TAB_EVENT,onOpen);
    return()=>window.removeEventListener(OPEN_MARK_TAB_EVENT,onOpen);
  },[myMarks2,marks]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{
    const sync=()=>{
      const slug=slugFromPath(window.location.pathname);
      if(!slug){setSelMark(null);return;}
      const m=marks.find(x=>markSlug(x)===slug)||myMarks2.find(x=>markSlug(x)===slug);
      if(m){setSelMark(m);setMpTab('jakt');}
    };
    sync();
    window.addEventListener('popstate',sync);
    return()=>window.removeEventListener('popstate',sync);
  },[marks,myMarks2]); // eslint-disable-line
  // Klick i flikraden (Jakt, Erbjud jakt, Min jakt): lämna öppen jaktsida och sökresultat,
  // annars låg de kvar ovanpå och klicket syntes inte.
  useEffect(()=>{
    if(!navTick)return;
    setAiRes(null);
    setSelJaktklubb(null);setSelJkMark(null);setSelAd(null);
    // Toppmenyn syns även på markens sida (desktop) — ett klick där lämnar marken.
    if(myMarkDetail){setMyMarkDetail(null);setMyMarkOpenAt(null);setAdsReload(n=>n+1);}
    if(slugFromPath(window.location.pathname)||adIdFromPath(window.location.pathname))window.history.replaceState(null,'','/'+window.location.search);
    setSelMark(null);
  },[navTick]); // eslint-disable-line
  const[favs,setFavs]=useState<Set<string>>(loadFavs);
  const toggleFav=(id:string)=>{if(!user){onLogin?.();return;}setFavs(prev=>{const n=new Set(prev);if(n.has(id))n.delete(id);else n.add(id);saveFavs(n);return n;});};
  const subscribeSel=(m:Mark)=>{api.subscribeMark(m.id).then(()=>{setSubMarkIds(prev=>{const n=new Set(prev);n.add(m.id);return n});setMyMarks2(prev=>[m,...prev])})};
  const unsubscribeSel=(m:Mark)=>{api.unsubscribeMark(m.id).then(()=>{setSubMarkIds(prev=>{const n=new Set(prev);n.delete(m.id);return n});setMyMarks2(prev=>prev.filter(x=>x.id!==m.id))})};

  const add=(l:HuntListing)=>setCart(p=>p.some(i=>i.listing.id===l.id)?p:[...p,{listing:l}]);
  const rm=(id:string)=>setCart(p=>p.filter(i=>i.listing.id!==id));
  const ic=(id:string)=>cart.some(i=>i.listing.id===id);
  const ft=ls.filter(l=>l.featured);const rs=ls.filter(l=>!l.featured);
  const filteredHunts=(()=>{
    let res=af!=='Alla'?ls:rs;
    if(huntTypeFilter!=='Alla') res=res.filter(l=>l.huntType===huntTypeFilter);
    if(huntSpeciesFilter!=='Alla') res=res.filter(l=>l.primaryGameType===huntSpeciesFilter||l.gameTypes.includes(huntSpeciesFilter));
    return res;
  })();
  // Menyns sökplats skapas av TopNav, som monteras om när en mark öppnas — hämta den efter varje rendering
  const[navSearchSlot,setNavSearchSlot]=useState<HTMLElement|null>(null);
  useLayoutEffect(()=>{const el=document.getElementById(NAV_SEARCH_SLOT_ID);if(el!==navSearchSlot)setNavSearchSlot(el);});
  // Områdesfilter (kartnålen i sökfältet, som på Qvestium)
  const[areaFilter,setAreaFilter]=useState<PickedArea|null>(null);
  const[showAreaPicker,setShowAreaPicker]=useState(false);
  // Kartnålens punkter: annonsernas marker och jakterna (jaktmarker listas inte som egen typ)
  const[adMarks,setAdMarks]=useState<Mark[]>([]);
  useEffect(()=>{api.publishedAds().then(a=>setAdMarks(marketplaceAds(a).map(x=>x.mark!))).catch(()=>{})},[adsReload]);
  const areaPoints=React.useMemo(()=>[...adMarks.map(m=>({lat:m.mapLat,lng:m.mapLng})),...ls.map(l=>({lat:l.mapLat,lng:l.mapLng}))].filter((p):p is {lat:number;lng:number}=>p.lat!=null&&p.lng!=null),[adMarks,ls]);
  const adInArea=React.useMemo(()=>areaFilter?(lat?:number|null,lng?:number|null)=>inArea(areaFilter,lat,lng):undefined,[areaFilter]);
  const areaLs=areaFilter?ls.filter(l=>inArea(areaFilter,l.mapLat,l.mapLng)):ls;
  // When AI search is active, show AI results; otherwise normal listings
  const showAi=!!aiRes;
  const aiList=(aiRes?.results??[]).filter(l=>!areaFilter||inArea(areaFilter,l.mapLat,l.mapLng));
  return(<>
      {/* AI-sökningen ligger i menyraden (som söket på Qvestium); logiken bor kvar här */}
      {navSearchSlot&&createPortal(<HeroSearch onOpenMap={()=>setShowAreaPicker(true)} mapActive={!!areaFilter} onResult={r=>{setAiRes(r);if(selMark)closeMark();if(myMarkDetail){setMyMarkDetail(null);setMyMarkOpenAt(null);}}} onClear={()=>setAiRes(null)} user={user} onOpenMeat={()=>window.open(QCARNE_URL,'_blank','noopener')} activeTab={mpTab} onAction={intent=>{
        // Handle AI chat actions
        const {action,params}=intent;
        if(action==='navigate'){
          const tab=params.tab as string;
          if(tab==='profil'){onNav?.('profil');}
          else if(tab==='viltkott'){window.open(QCARNE_URL,'_blank','noopener');}
          else if(tab==='minajakt'||tab==='jakt'||tab==='arrende'){setMpTab(tab);}
        }else if(action==='start_hunt'){
          if(params.markId){
            const m=marks.find(mk=>mk.id===params.markId)||myMarks2.find(mk=>mk.id===params.markId);
            if(m)setMyMarkDetail(m);
          }else{setMpTab('minajakt');}
        }else if(action==='create_plan'){
          if(params.markId){
            const m=marks.find(mk=>mk.id===params.markId)||myMarks2.find(mk=>mk.id===params.markId);
            if(m)setMyMarkDetail(m);
          }else{setMpTab('minajakt');}
        }else if(action==='sell_meat'){
          window.open(QCARNE_URL,'_blank','noopener');
        }else if(action==='create_event'){
          if(params.markId){
            const m=marks.find(mk=>mk.id===params.markId)||myMarks2.find(mk=>mk.id===params.markId);
            if(m)setMyMarkDetail(m);
          }else{setMpTab('minajakt');}
        }else if(action==='open_mark'){
          if(params.markId){
            const m=marks.find(mk=>mk.id===params.markId)||myMarks2.find(mk=>mk.id===params.markId);
            if(m){openMark(m);}
          }else{setMpTab('jakt');setJaktSubTab(DEFAULT_JAKT_SUB_TAB);}
        }else if(action==='open_listing'){
          if(params.listingId){
            const found=ls.find(l=>l.id===params.listingId)||aiRes?.results?.find(l=>l.id===params.listingId);
            if(found){setSel(found);}
          }
        }else if(action==='register_mark'){
          setWizardInitData(undefined);setShowMarkWizard(true);
        }
      }}/>,navSearchSlot)}
    {myMarkDetail?<MyMarkDetailView key={myMarkDetail.id} mark={myMarkDetail} huntMarks={huntMarks} user={user} onOpenPublicPage={marks.some(m=>m.id===myMarkDetail.id&&m.isPublished)?()=>{const m=marks.find(x=>x.id===myMarkDetail.id)!;setMyMarkDetail(null);setMyMarkOpenAt(null);setMpTab('jakt');openMark(m);window.scrollTo({top:0});}:undefined} initialTab={myMarkOpenAt?.tab} tabRequest={myMarkOpenAt?.seq} mapFullscreenRequest={myMarkOpenAt?.mapFullscreen?myMarkOpenAt.seq:undefined} initialGroupId={myMarkOpenAt?.groupId} initialAdId={myMarkOpenAt?.adId} onBack={()=>{setMyMarkDetail(null);setMyMarkOpenAt(null);setAdsReload(n=>n+1);}} onDelete={id=>{setMarks(prev=>prev.filter(m=>m.id!==id));setMyMarks2(prev=>prev.filter(m=>m.id!==id));setMyCalEvents(prev=>prev.filter(e=>e.markId!==id));setMyMarkDetail(null);}} isAdmin={isAdmin} onCalEventCreated={ev=>{setMyCalEvents(prev=>[...prev,ev].sort((a,b)=>new Date(a.startTime).getTime()-new Date(b.startTime).getTime()))}} pendingJoinMeetingId={joinMarkId&&myMarkDetail?.id===joinMarkId?joinMeetingId:null} userAvatarUrl={userAvatarUrl} onJaktklubSettingsChanged={()=>{setJaktklubbarLoaded(false);setJaktklubbarList([]);}} onMarkImageChanged={m=>{setMyMarks2(prev=>prev.map(x=>x.id===m.id?{...x,imageUrl:m.imageUrl}:x));setMarks(prev=>prev.map(x=>x.id===m.id?{...x,imageUrl:m.imageUrl}:x));}}/>:
    selJaktklubb&&selJkMark?<MarkPage mark={selJkMark} jaktklubAd={selJaktklubb} user={user} onBack={()=>{setSelJaktklubb(null);setSelJkMark(null);}} backLabel="Jaktklubbar" onLogin={onLogin} fav={favs.has(selJkMark.id)} onFav={()=>toggleFav(selJkMark.id)} onOpenAd={openAd}/>:
    selAd&&selAd.mark?<MarkPage mark={selAd.imageUrl?{...selAd.mark,imageUrl:selAd.imageUrl}:selAd.mark} ad={selAd} user={user} onBack={closeAd} backLabel={AD_KIND_LABEL[selAd.kind]} onLogin={onLogin} fav={favs.has(selAd.id)} onFav={()=>toggleFav(selAd.id)} onEditAd={()=>openMarkTab(selAd.markId,'annonser',{adId:selAd.id},selAd.mark)}/>:
    selMark?<MarkPage mark={selMark} onOpenAd={openAd} user={user} isAdmin={isAdmin} onBack={closeMark} backLabel={jaktListLabel} onEdit={isAdmin?()=>setEditMark(selMark):undefined} onDelete={isAdmin?()=>{setMarks(prev=>prev.filter(m=>m.id!==selMark.id));closeMark();}:undefined} isSubscribed={subMarkIds.has(selMark.id)} onSubscribe={()=>subscribeSel(selMark)} onUnsubscribe={()=>unsubscribeSel(selMark)} onLogin={onLogin} fav={favs.has(selMark.id)} onFav={()=>toggleFav(selMark.id)}/>:
    <div style={{minHeight:'100vh',paddingTop:`calc(${NAV_MAIN_H}px + var(--qv-tabs-h, ${NAV_TABS_H}px) + env(safe-area-inset-top))`}}>

      {areaFilter&&<div style={{maxWidth:1264,margin:'0 auto',padding:'16px clamp(12px,3vw,32px) 0'}}><AreaFilterChip area={areaFilter} onEdit={()=>setShowAreaPicker(true)} onClear={()=>setAreaFilter(null)}/></div>}
      {showAreaPicker&&<AreaPickerModal initial={areaFilter} points={areaPoints} onApply={a=>{setAreaFilter(a);setShowAreaPicker(false);if(mpTab!=='jakt')setMpTab('jakt');}} onClear={()=>{setAreaFilter(null);setShowAreaPicker(false);}} onClose={()=>setShowAreaPicker(false)}/>}

      {/* AI search results */}
      {showAi&&(
        <div style={{padding:'40px clamp(12px,3vw,32px) 80px',maxWidth:1264,margin:'0 auto'}}>
          <div style={{...secT,marginBottom:20,display:'flex',alignItems:'center',gap:10}}>
            <span style={{color:T.amber}}>—</span>
            <span>Sökresultat · {aiList.length} {aiList.length===1?'jakt':'jakter'}</span>
          </div>
          {aiList.length===0
            ?<Empty emoji="" title="Inga matchande jakter" sub="Prova bredare sökord, t.ex. 'älg' eller 'Dalarna'"/>
            :<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',gap:16}}>{aiList.map(l=><HuntCard key={l.id} listing={l} inCart={ic(l.id)} onAddToCart={()=>add(l)} onClick={()=>setSel(l)}/>)}</div>
          }
        </div>
      )}

      {/* Normal listings — hidden when AI search active */}
      {!showAi&&(
        <>
          <div style={mpTab==='jakt'?{padding:mob?'16px 16px 96px':'24px 28px 64px',maxWidth:1440,margin:'0 auto'}:{padding:'20px clamp(12px,3vw,32px) 80px',maxWidth:1264,margin:'0 auto'}}>
            {mpTab==='minajakt'&&<div style={{maxWidth:1264,margin:'0 auto',padding:mob?'4px 0':'8px 0'}}>
              {/* ── Mina jaktmarker (skiss A) ── */}
              <div style={{...TYPE.small,color:T.textMuted}}>Min jakt</div>
              <h1 style={{...TYPE.page,fontSize:mob?28:32,color:T.text,margin:'2px 0 20px'}}>Mina jaktmarker</h1>
              {user&&<AfterHuntReminder/>}
              {/* MJ1: en låg rad per mark (views/minjakt/MarkList.tsx) */}
              {myMarks2.length>0&&<div style={{maxWidth:760,marginBottom:18}}>
                {[...myMarks2].sort((a,b)=>{
                  if(a.id===homeMarkId)return -1;
                  if(b.id===homeMarkId)return 1;
                  const ai=markOrderPref.indexOf(a.id),bi=markOrderPref.indexOf(b.id);
                  return(ai===-1?999:ai)-(bi===-1?999:bi);
                }).map((m,i)=>{
                  const isHome=m.id===homeMarkId;
                  const isOwner=!!((user?.sub&&m.ownerUserId&&user.sub===m.ownerUserId)||(user?.email&&m.contactEmail&&user.email.toLowerCase()===m.contactEmail.toLowerCase()));
                  return <MarkListRow key={m.id} mark={m} first={i===0} unread={markListExtras.unread[m.id]??0} images={markListExtras.images[m.id]??[]} maxImages={mob?2:3}
                    next={nextHunt(myCalEvents,m.id)} role={isOwner?'Markägare':m.isDemo?'Bara läsning':undefined} isHome={isHome}
                    onOpen={()=>setMyMarkDetail(m)}
                    onToggleHome={()=>{const nid=isHome?null:m.id;setHomeMarkId(nid);if(nid)localStorage.setItem('qvenari_home_mark',nid);else localStorage.removeItem('qvenari_home_mark');}}/>;
                })}
              </div>}
              {myMarks2.length>0
                ?<div style={{maxWidth:760,display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap',border:`1px dashed ${T.borderHover}`,borderRadius:RADIUS.lg,padding:'12px 16px'}}>
                  <span style={{...TYPE.small,color:T.textMuted}}>Gå med i fler jaktmarker på Jakt.</span>
                  <Button variant="secondary" pill onClick={()=>{setMpTab('jakt');setJaktSubTab(DEFAULT_JAKT_SUB_TAB);}}>Utforska jaktlag</Button>
                </div>
                :<div style={{display:'flex',alignItems:'center',justifyContent:'center',border:`1px dashed ${T.borderHover}`,borderRadius:RADIUS.lg,maxWidth:760}}>
                  <EmptyState icon={MapPin} title="Du har inga jaktmarker ännu" sub="Hitta jaktlag och marker på Jakt och välj Gå med."
                    action={<div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8}}>
                      <Button variant="secondary" pill onClick={()=>{setMpTab('jakt');setJaktSubTab(DEFAULT_JAKT_SUB_TAB);}}>Utforska jaktlag</Button>
                      <ShowDemoMarkButton onShown={ms=>{setMyMarks2(ms);setSubMarkIds(new Set(ms.map(m=>m.id)));}}/>
                    </div>}/>
                </div>}
              {/* ── Godkända ansökningar (approved, contract_sent, contract_signed) ── */}
              {(()=>{
                const pendingApps=myApps.filter(a=>['approved','contract_sent','contract_signed','dispensation_granted'].includes(a.status));
                if(!pendingApps.length)return null;
                const stMap:Record<string,{label:string;tone:BadgeTone;action:string}>={
                  'approved':{label:'Godkänd',tone:'ok',action:'Signera avtal'},
                  'dispensation_granted':{label:'Dispens beviljad',tone:'ok',action:'Signera avtal'},
                  'contract_sent':{label:'Kontrakt skickat',tone:'info',action:'Signera avtal'},
                  'contract_signed':{label:'Kontrakt signerat',tone:'ok',action:'Betala med kort'},
                };
                return(
                  <section style={{marginTop:34}}>
                    <SectionHeader title="Godkända ansökningar"/>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(min(340px,100%),1fr))',gap:14}}>
                      {pendingApps.map(app=>{
                        const mark=marks.find(m=>m.id===app.markId);
                        const mName=mark?.name||'Okänd mark';
                        const gt:string[]=mark?(() => { try { return JSON.parse(mark.gameTypesJson||'[]'); } catch { return []; } })():[];
                        const st=stMap[app.status]||{label:app.status,tone:'neutral' as BadgeTone,action:'Visa'};
                        return(
                          <Card key={app.id} padding={16}>
                            <div style={{display:'flex',alignItems:'flex-start',gap:12}}>
                              {mark&&<img src={markImg(mark)} alt="" style={{width:52,height:52,objectFit:'cover',borderRadius:RADIUS.md,flexShrink:0}} onError={e=>{(e.target as HTMLImageElement).src=GREEN_PLACEHOLDER}}/>}
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{...TYPE.title,fontSize:16,color:T.text}}>{mName}</div>
                                <div style={{...TYPE.small,color:T.textMuted,marginTop:2}}>{[mark?.municipality||mark?.county,mark?.areaHa?`${mark.areaHa} ha`:''].filter(Boolean).join(' · ')}</div>
                                {gt.length>0&&<div style={{display:'flex',gap:4,marginTop:8,flexWrap:'wrap'}}>{gt.slice(0,3).map((g:string)=><Badge key={g} tone="accent">{g}</Badge>)}</div>}
                              </div>
                              <Badge tone={st.tone}>{st.label}</Badge>
                            </div>
                            <Button block style={{marginTop:14}} onClick={()=>setAppContractModal({app,mark:mark||null})}>{st.action}</Button>
                          </Card>
                        );
                      })}
                    </div>
                  </section>
                );
              })()}
              {/* ── Kommande + kalender ── */}
              {(()=>{
                const MONTHS=['Januari','Februari','Mars','April','Maj','Juni','Juli','Augusti','September','Oktober','November','December'];
                const DAYS=['Mån','Tis','Ons','Tor','Fre','Lör','Sön'];
                const now2=new Date();
                const futureEvs=myCalEvents.filter(e=>new Date(e.startTime)>=now2).sort((a,b)=>new Date(a.startTime).getTime()-new Date(b.startTime).getTime());
                const visK=showAllKommande?futureEvs:futureEvs.slice(0,5);
                const markName=(id:string)=>myMarks2.find(m=>m.id===id)?.name;
                const calendar=(()=>{
                  const firstDay=new Date(calViewYear,calViewMonth,1);
                  const daysInMonth=new Date(calViewYear,calViewMonth+1,0).getDate();
                  // Monday-first offset (0=Mon ... 6=Sun)
                  let startOffset=firstDay.getDay()-1; if(startOffset<0)startOffset=6;
                  const todayStr=new Date().toISOString().slice(0,10);
                  const eventDayMap:Record<string,MarkCalendarEvent[]>={};
                  myCalEvents.forEach(ev=>{const d=ev.startTime.slice(0,10);eventDayMap[d]=[...(eventDayMap[d]||[]),ev];});
                  const prevMonth=()=>{if(calViewMonth===0){setCalViewMonth(11);setCalViewYear(y=>y-1);}else{setCalViewMonth(m=>m-1);}};
                  const nextMonth=()=>{if(calViewMonth===11){setCalViewMonth(0);setCalViewYear(y=>y+1);}else{setCalViewMonth(m=>m+1);}};
                  const cells:Array<{day:number|null;dateStr:string|null}>=[];
                  for(let i=0;i<startOffset;i++)cells.push({day:null,dateStr:null});
                  for(let d=1;d<=daysInMonth;d++){
                    const ds=`${calViewYear}-${String(calViewMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                    cells.push({day:d,dateStr:ds});
                  }
                  while(cells.length%7!==0)cells.push({day:null,dateStr:null});
                  return(
                    <Card padding={12} style={{maxWidth:420,marginBottom:14}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                        <IconButton label="Föregående månad" onClick={prevMonth}><Icon as={ChevronLeft}/></IconButton>
                        <span style={{fontWeight:700,color:T.text,fontSize:15}}>{MONTHS[calViewMonth]} {calViewYear}</span>
                        <IconButton label="Nästa månad" onClick={nextMonth}><Icon as={ChevronRight}/></IconButton>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',padding:'6px 0 4px'}}>
                        {DAYS.map(d=><div key={d} style={{textAlign:'center',fontSize:12,fontWeight:700,color:T.textMuted,padding:'4px 0'}}>{d}</div>)}
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2}}>
                        {cells.map((c,i)=>{
                          if(!c.day)return <div key={i}/>;
                          const isToday=c.dateStr===todayStr;
                          const evs=c.dateStr?eventDayMap[c.dateStr]:null;
                          const hasEvs=!!evs&&evs.length>0;
                          const completed=evs?.find(e=>e.status==='completed');
                          const cell:React.CSSProperties={height:40,display:'flex',alignItems:'center',justifyContent:'center',position:'relative',borderRadius:RADIUS.sm,fontSize:14,fontWeight:isToday||hasEvs?700:500,color:isToday||hasEvs?T.text:T.textSub,background:completed?T.okSoft:hasEvs?T.accentSoft:'transparent',border:`1px solid ${isToday?T.amber:'transparent'}`,fontFamily:'inherit',padding:0};
                          const dot=hasEvs&&<span aria-hidden style={{position:'absolute',bottom:4,left:'50%',transform:'translateX(-50%)',width:5,height:5,borderRadius:'50%',background:completed?T.ok:T.amber}}/>;
                          const title=hasEvs?evs!.map(e=>`${e.title}${e.status==='completed'?' (Genomförd)':''}`).join('\n'):undefined;
                          return completed
                            ?<button key={i} type="button" title={title} onClick={()=>setDashCompletedHunt(completed)} style={{...cell,cursor:'pointer'}}>{c.day}{dot}</button>
                            :<div key={i} title={title} style={cell}>{c.day}{dot}</div>;
                        })}
                      </div>
                    </Card>
                  );
                })();
                return(
                  <section style={{marginTop:34}}>
                    <SectionHeader title="Kommande" action={<TextLink onClick={()=>setShowCal(v=>!v)} aria-expanded={showCal}>{showCal?'Dölj kalender':'Visa kalender →'}</TextLink>}/>
                    {showCal&&calendar}
                    <Card padding={0} style={{padding:'6px 18px'}}>
                      {futureEvs.length===0
                        ?<div style={{...TYPE.small,fontSize:14,color:T.textMuted,padding:'14px 0'}}>Inga kommande händelser på dina marker.</div>
                        :visK.map((ev,i)=>(
                          <EventRow key={ev.id} ev={ev} first={i===0}
                            sub={[markName(ev.markId),ev.location,timeOf(ev.startTime)].filter(Boolean).join(' · ')}
                            trailing={<EventTypeBadge type={ev.eventType}/>}/>
                        ))}
                    </Card>
                    {!showAllKommande&&futureEvs.length>5&&<div style={{textAlign:'center',marginTop:6}}><TextLink onClick={()=>setShowAllKommande(true)}>Visa fler ({futureEvs.length-5})</TextLink></div>}
                  </section>
                );
              })()}
              {user&&<MyPurchases/>}
              {user&&<MyLegacyApplications/>}
              {/* ── Mina jakter (bokningar) ── */}
              <section style={{marginTop:34}}>
                <SectionHeader title="Mina jakter"/>
                {myBookings.length===0?<Card padding={0}><EmptyState icon={Crosshair} title="Inga bokade jakter" sub="Boka en jakt under Jakt → Enskilda jakter." action={<Button variant="secondary" pill onClick={()=>{setMpTab('jakt');setJaktSubTab('enskilda');}}>Hitta jakter</Button>}/></Card>:(()=>{
                  const PAGE_SIZE=15;
                  const recentCount=3;
                  const visibleBookings=!showAllJakter
                    ?myBookings.slice(0,recentCount)
                    :myBookings.slice((jakterPage-1)*PAGE_SIZE,jakterPage*PAGE_SIZE);
                  const totalPages=Math.ceil(myBookings.length/PAGE_SIZE);
                  return(<>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(min(280px,100%),1fr))',gap:16}}>
                      {visibleBookings.map(b=>{
                        const h=b.listing;
                        const st:{l:string;t:BadgeTone}=b.status==='signed'?{l:'Signerat',t:'ok'}:b.status==='pending'?{l:'Väntar',t:'accent'}:{l:'Okänd',t:'neutral'};
                        return h?<button key={b.id} type="button" onClick={()=>setSel(h)} style={{display:'block',padding:0,textAlign:'left',fontFamily:'inherit',color:T.text,background:T.surface,borderRadius:RADIUS.lg,overflow:'hidden',cursor:'pointer',border:`1px solid ${T.border}`}}>
                          {h.imageUrl&&<img src={h.imageUrl} style={{width:'100%',height:160,objectFit:'cover',display:'block'}} alt=""/>}
                          <div style={{padding:'14px 16px 16px'}}>
                            <div style={{...TYPE.title,fontSize:16}}>{h.title}</div>
                            <div style={{...TYPE.small,color:T.textMuted,marginTop:2}}>{h.location} · {h.region}</div>
                            <div style={{display:'flex',gap:8,marginTop:10,alignItems:'center'}}>
                              <Badge tone={st.t}>{st.l}</Badge>
                              <span style={{...TYPE.small,color:T.textMuted}}>{new Date(b.createdAt).toLocaleDateString('sv-SE')}</span>
                              {h.price>0&&<span style={{marginLeft:'auto',color:T.accentText,fontWeight:700,fontSize:14}}>{h.price} {h.priceUnit||'kr'}</span>}
                            </div>
                          </div>
                        </button>:null;
                      })}
                    </div>
                    {!showAllJakter&&myBookings.length>recentCount&&(
                      <div style={{marginTop:16,textAlign:'center'}}>
                        <Button variant="secondary" pill onClick={()=>{setShowAllJakter(true);setJakterPage(1);}}>Se tidigare jakter ({myBookings.length - recentCount} till)</Button>
                      </div>
                    )}
                    {showAllJakter&&(
                      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginTop:20,flexWrap:'wrap'}}>
                        {totalPages>1&&<>
                          <IconButton label="Föregående sida" onClick={()=>setJakterPage(p=>Math.max(1,p-1))} disabled={jakterPage===1}><Icon as={ChevronLeft}/></IconButton>
                          {Array.from({length:totalPages},(_,i)=>i+1).map(p=>(
                            <Button key={p} variant={p===jakterPage?'primary':'ghost'} aria-current={p===jakterPage?'page':undefined} onClick={()=>setJakterPage(p)} style={{minWidth:44,padding:0}}>{p}</Button>
                          ))}
                          <IconButton label="Nästa sida" onClick={()=>setJakterPage(p=>Math.min(totalPages,p+1))} disabled={jakterPage===totalPages}><Icon as={ChevronRight}/></IconButton>
                          <span style={{...TYPE.small,color:T.textMuted,marginLeft:4}}>Sida {jakterPage} av {totalPages}</span>
                        </>}
                        <TextLink onClick={()=>{setShowAllJakter(false);setJakterPage(1);}}>Dölj</TextLink>
                      </div>
                    )}
                  </>);
                })()}
              </section>
              {/* ── Genomförda jakter ── */}
              {(()=>{
                const now3=new Date();
                const pastEvs=myCalEvents.filter(e=>e.status==='completed'||new Date(e.startTime)<now3).sort((a,b)=>new Date(b.startTime).getTime()-new Date(a.startTime).getTime());
                const visG=showAllGenom?pastEvs:pastEvs.slice(0,5);
                if(!pastEvs.length)return null;
                return(
                  <section style={{marginTop:34}}>
                    <SectionHeader title="Genomförda jakter"/>
                    <Card padding={0} style={{padding:'6px 18px'}}>
                      {visG.map((ev,i)=>(
                        <EventRow key={ev.id} ev={ev} first={i===0} tone="ok" onClick={()=>setDashCompletedHunt(ev)}
                          sub={[myMarks2.find(m=>m.id===ev.markId)?.name,ev.location].filter(Boolean).join(' · ')}
                          trailing={<Badge tone="ok">Genomförd</Badge>}/>
                      ))}
                    </Card>
                    {!showAllGenom&&pastEvs.length>5&&<div style={{textAlign:'center',marginTop:6}}><TextLink onClick={()=>setShowAllGenom(true)}>Visa fler ({pastEvs.length-5})</TextLink></div>}
                  </section>
                );
              })()}
              {/* Completed hunt detail modal */}
              {dashCompletedHunt&&<CompletedHuntModal event={dashCompletedHunt} onClose={()=>{setDashCompletedHunt(null);setDashUpdateGoals(false)}} onUpdateGoals={async(kills)=>{
                const evMark=myMarks2.find(m=>m.id===dashCompletedHunt.markId);
                if(!evMark)return;
                try{
                  const todayIso=new Date().toISOString().slice(0,10);
                  const curSeason=(()=>{const m=new Date().getMonth();const y=new Date().getFullYear();return m>=6?`${y}/${y+1}`:`${y-1}/${y}`;})();
                  const plan=await api.markShootingPlan(evMark.id);
                  for(const k of kills){
                    let existing=plan.find(p=>p.gameType===k.gameType);
                    if(!existing){const wantFk=planSpeciesFamilyKey(k.gameType);existing=plan.find(p=>planSpeciesFamilyKey(p.gameType)===wantFk);}
                    if(existing){
                      const newActual=(existing.actualCount||0)+k.count;
                      await api.upsertMarkShootingPlan({markId:evMark.id,season:existing.season,gameType:existing.gameType,plannedCount:existing.plannedCount,actualCount:newActual,costPerAnimal:existing.costPerAnimal,notes:`Uppdaterad efter jakt ${new Date().toLocaleDateString('sv-SE')} (+${k.count} fällt)`});
                    }
                    const viltRegGt=k.gameType.includes(' — ')?k.gameType.split(' — ')[0]:k.gameType;
                    const meetingId=dashCompletedHunt.huntMeetingId??dashCompletedHunt.qmeetMeetingId; // EJ5: rätt jakts rapport
                    for(let i=0;i<k.count;i++){await api.addViltRegistration({markId:evMark.id,season:curSeason,gameType:viltRegGt,shotDate:todayIso,meetingId}).catch(()=>{});}
                  }
                  notifyHuntEconomyChanged();
                  setDashUpdateGoals(false);
                }catch(err){console.error('Goal update error',err)}
              }}/>}
            </div>}
            {mpTab==='jakt'&&<div>
              <JaktHeader sub={jaktSubTab} onSub={setJaktSubTab}/>
                            {/* Annonser — skapas från markens priser och uppgifter under Min jakt → marken → Annonser */}
              {jaktSubTab==='jaktlag'&&<AdList key="jaktlag" kind="jaktlag" user={user} onOpen={openAd} onLogin={onLogin} reloadKey={adsReload} inArea={adInArea}/>}
              {jaktSubTab==='enskilda'&&<AdList key="enskild" kind="enskild" user={user} onOpen={openAd} onLogin={onLogin} reloadKey={adsReload} inArea={adInArea}/>}
              {jaktSubTab==='jaktklubbar'&&<AdList key="jaktklubb" kind="jaktklubb" user={user} onOpen={openAd} onLogin={onLogin} reloadKey={adsReload} inArea={adInArea}/>}
              {jaktSubTab==='jaktpaket'&&<AdList key="jaktpaket" kind="jaktpaket" user={user} onOpen={openAd} onLogin={onLogin} reloadKey={adsReload} inArea={adInArea}/>}
            </div>}
            {mpTab==='arrende'&&<ArrendeChat user={user??null} isAdmin={isAdmin} onOpenWizard={(preData)=>{setWizardInitData(preData);setShowMarkWizard(true);}} onMarkCreated={()=>{api.myMarks().then(d=>{setMyMarks2(d);setSubMarkIds(new Set(d.map(m=>m.id)))}).catch(()=>{});setMpTab('minajakt');}}/>}
          </div>
        </>
      )}

    </div>}
    {huntCartOpen&&<CartSidebar cart={cart} onRemove={rm} onClose={()=>onSetHuntCartOpen(false)} onBook={l=>{onSetHuntCartOpen(false);setSel(l)}}/>}
    {sel&&<DetailModal listing={sel} onClose={()=>setSel(null)} user={user} isAdmin={isAdmin} onEdit={isAdmin?()=>{setEditListing(sel);setSel(null)}:undefined} onDelete={isAdmin?()=>{setLs(prev=>prev.filter(x=>x.id!==sel.id));setSel(null);}:undefined} mark={marks.find(m=>m.id===sel.markId)||null} onLogin={onLogin||(() => {})}/>}
    {editMark&&<EditMarkModal mark={editMark} onClose={()=>setEditMark(null)} onSaved={updated=>{setMarks(prev=>prev.map(m=>m.id===updated.id?updated:m));setSelMark(cur=>cur&&cur.id===updated.id?updated:cur);setEditMark(null)}}/>}
    {editListing&&<EditListingModal listing={editListing} onClose={()=>setEditListing(null)} onSaved={updated=>{setLs(prev=>prev.map(l=>l.id===updated.id?updated:l));refreshListings()}}/>}
    {showMarkWizard&&<MarkRegistrationWizard user={user??null} initialData={wizardInitData} onClose={()=>{setShowMarkWizard(false);setWizardInitData(undefined);}} onComplete={(newMark)=>{setShowMarkWizard(false);setWizardInitData(undefined);if(newMark){setMarks(prev=>[...prev,newMark]);}api.myMarks().then(d=>{setMyMarks2(d);setSubMarkIds(new Set(d.map(m=>m.id)))}).catch(()=>{});setMpTab('minajakt');}}/>}
    {/* ── Application Contract Modal (sign + Stripe) ── */}
    {appContractModal&&(()=>{
      const{app,mark:aMark}=appContractModal;
      const contract=app.contract;
      const mName=aMark?.name||'Jaktmark';
      const closeModal=()=>setAppContractModal(null);
      const refreshApps=()=>{api.myApplications().then(setMyApps).catch(()=>{});api.myMarks().then(d=>{setMyMarks2(d);setSubMarkIds(new Set(d.map(m=>m.id)))}).catch(()=>{});closeModal();};

      // No contract yet — show waiting message
      if(!contract){
        return(
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>e.target===e.currentTarget&&closeModal()}>
            <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,padding:28,width:'100%',maxWidth:500,textAlign:'center'}}>
              <div style={{fontSize:18,fontWeight:700,color:T.text,marginBottom:12}}>Ansökan godkänd!</div>
              <div style={{color:T.textSub,fontSize:14,marginBottom:8}}>Din ansökan till <strong style={{color:T.amber}}>{mName}</strong> är godkänd.</div>
              <div style={{color:T.textSub,fontSize:13,marginBottom:24}}>Invänta att admin skickar kontraktet för signering.</div>
              <button onClick={closeModal} style={{background:T.amber,color:'#000',border:'none',borderRadius:5,padding:'11px 24px',fontWeight:700,cursor:'pointer'}}>Stäng</button>
            </div>
          </div>
        );
      }

      // Contract exists — sign or pay
      return <AppContractFlow contract={contract} markName={mName} onClose={closeModal} onComplete={refreshApps}/>;
    })()}

    {/* ── Admin: pågående jaktmöten (Qmeet) ── */}
    {adminOngoingHuntsOpen&&isAdmin&&(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.78)',zIndex:3200,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>{if(e.target===e.currentTarget)setAdminOngoingHuntsOpen(false);}}>
        <div style={{background:'#1e2a1e',border:`1px solid ${T.amber}`,borderRadius:12,maxWidth:480,width:'100%',maxHeight:'min(85vh, 560px)',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.65)'}} onClick={e=>e.stopPropagation()}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 18px',borderBottom:`1px solid ${T.border}`}}>
            <div style={{fontSize:17,fontWeight:800,color:T.amber}}>Pågående jaktmöten</div>
            <button type="button" onClick={()=>setAdminOngoingHuntsOpen(false)} style={{background:'none',border:'none',color:T.textSub,cursor:'pointer',fontSize:22,lineHeight:1,padding:4}} aria-label="Stäng">✕</button>
          </div>
          <div style={{padding:'12px 18px 0',color:T.textSub,fontSize:12,lineHeight:1.45}}>
            Möten med status <b style={{color:T.text}}>planerad</b> eller <b style={{color:T.text}}>aktiv</b> i Qmeet. Stäng kopplar från LiveKit och avslutar mötet för alla deltagare.
          </div>
          <div style={{flex:1,overflowY:'auto',padding:'14px 18px 18px',display:'flex',flexDirection:'column',gap:10}}>
            {adminOngoingMeetingsLd&&<div style={{color:T.textSub,fontSize:14}}>Laddar…</div>}
            {adminOngoingMeetingsErr&&<div style={{color:'#e74c3c',fontSize:13}}>{adminOngoingMeetingsErr}</div>}
            {!adminOngoingMeetingsLd&&!adminOngoingMeetingsErr&&adminOngoingMeetings.length===0&&<div style={{color:T.textSub,fontSize:14}}>Inga pågående möten just nu.</div>}
            {!adminOngoingMeetingsLd&&adminOngoingMeetings.map(m=>{
              const created=m.created_at!=null?new Date(typeof m.created_at==='number'&&m.created_at<1e12?m.created_at*1000:m.created_at).toLocaleString('sv-SE',{dateStyle:'short',timeStyle:'short'}):'';
              return(
                <div key={m.id} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,padding:'12px 14px'}}>
                  <div style={{fontWeight:700,color:T.text,fontSize:14,marginBottom:4,lineHeight:1.3}}>{m.title||m.id}</div>
                  <div style={{fontSize:12,color:T.textSub,marginBottom:8}}>
                    {m.status==='active'?'Aktiv':'Planerad'}{m.host_name?` · ${m.host_name}`:''}{created?` · ${created}`:''}
                  </div>
                  <div style={{fontFamily:'ui-monospace,monospace',fontSize:10,color:T.textMuted,wordBreak:'break-all',marginBottom:10}}>{m.id}</div>
                  <button type="button" disabled={closingMeetingId===m.id} onClick={async()=>{
                    if(!confirm(`Stäng mötet "${m.title}"? Alla i jaktläget kopplas bort.`))return;
                    setClosingMeetingId(m.id);
                    try{
                      const r=await fetch(qmeetApi(`/api/meetings/${m.id}/end`),{method:'POST',headers:{'Content-Type':'application/json',...bearerHeaders()},body:'{}'});
                      if(!r.ok){const j=await r.json().catch(()=>({}))as {error?:string};throw new Error(j.error||`HTTP ${r.status}`);}
                      setAdminOngoingMeetings(prev=>prev.filter(x=>x.id!==m.id));
                    }catch(e:unknown){alert((e as Error).message||'Kunde inte stänga mötet');}
                    finally{setClosingMeetingId(null);}
                  }} style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'none',background:'rgba(192,57,43,0.9)',color:'#fff',fontWeight:700,fontSize:13,cursor:closingMeetingId===m.id?'wait':'pointer',fontFamily:'inherit',opacity:closingMeetingId===m.id?0.65:1}}>
                    {closingMeetingId===m.id?'Stänger…':'Stäng möte'}
                  </button>
                </div>
              );
            })}
          </div>
          <div style={{display:'flex',gap:10,padding:'0 18px 18px'}}>
            <button type="button" onClick={()=>void loadAdminOngoingMeetings()} style={{flex:1,padding:'11px',borderRadius:8,border:`1px solid ${T.amber}`,background:'transparent',color:T.amber,fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>Uppdatera lista</button>
            <button type="button" onClick={()=>setAdminOngoingHuntsOpen(false)} style={{flex:1,padding:'11px',borderRadius:8,border:'none',background:T.amber,color:'#000',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>Klar</button>
          </div>
        </div>
      </div>
    )}

    {/* ── Admin: Markägaravtal (Erbjud jakt) ── */}
    {adminMarkContractsOpen&&isAdmin&&(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.78)',zIndex:3200,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>{if(e.target===e.currentTarget){setAdminMarkContractsOpen(false);setSigningContractId(null);}}}>
        <div style={{background:'#1e2a1e',border:`1px solid ${T.amber}`,borderRadius:12,maxWidth:640,width:'100%',maxHeight:'min(90vh, 700px)',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.65)'}} onClick={e=>e.stopPropagation()}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px',borderBottom:`1px solid ${T.border}`}}>
            <div style={{fontSize:17,fontWeight:800,color:T.amber}}>Markägaravtal — Erbjud jakt</div>
            <button type="button" onClick={()=>{setAdminMarkContractsOpen(false);setSigningContractId(null);}} style={{background:'none',border:'none',color:T.textSub,cursor:'pointer',fontSize:22,lineHeight:1,padding:4}}>✕</button>
          </div>
          <div style={{flex:1,overflowY:'auto',padding:'14px 20px 20px',display:'flex',flexDirection:'column',gap:12}}>
            {adminMarkContractsLd&&<div style={{color:T.textSub,fontSize:14}}>Laddar…</div>}
            {adminMarkContractsErr&&<div style={{color:'#e74c3c',fontSize:13}}>{adminMarkContractsErr}</div>}
            {!adminMarkContractsLd&&!adminMarkContractsErr&&adminMarkContracts.length===0&&<div style={{color:T.textSub,fontSize:14}}>Inga avtal inkomna ännu.</div>}
            {!adminMarkContractsLd&&adminMarkContracts.map(c=>{
              const isPending=c.status==='pending_admin';
              const signed=new Date(c.signedAt).toLocaleString('sv-SE',{dateStyle:'short',timeStyle:'short'});
              return(
                <div key={c.id} style={{background:T.surface,border:`1px solid ${isPending?T.amber:T.border}`,borderRadius:8,padding:'14px 16px'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6,flexWrap:'wrap',gap:8}}>
                    <div style={{fontWeight:700,color:T.text,fontSize:14}}>{c.markName}</div>
                    <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:12,background:isPending?'rgba(238,143,9,0.15)':'rgba(46,125,50,0.15)',color:isPending?T.amber:'#4caf50'}}>{isPending?'Väntar på admin':'Publicerad'}</span>
                  </div>
                  <div style={{fontSize:12,color:T.textSub,lineHeight:1.7}}>
                    <div>{c.areaHa} ha{c.county?`, ${c.county}`:''}{c.fastighetsBeteckning?` · ${c.fastighetsBeteckning}`:''}</div>
                    <div>Ägare: <strong style={{color:T.text}}>{c.registrantEmail}</strong> ({c.registrantName})</div>
                    <div>Signerat av ägare: <strong style={{color:T.text}}>{c.signatureName}</strong> — {signed}</div>
                    {c.adminSignatureName&&<div style={{color:'#4caf50'}}>Admin-signerat av: {c.adminSignatureName}</div>}
                  </div>
                  {/* Avskjutningsplan */}
                  {(()=>{try{const plan=JSON.parse(c.avskjutningsplanJson);if(plan.length>0)return(<div style={{marginTop:8,fontSize:11,color:T.textMuted}}>{plan.map((p:any)=>`${p.art}: stam ${p.stam}, uttag ${p.uttakTot}`).join(' · ')}</div>);}catch{}return null;})()}
                  {isPending&&(
                    signingContractId===c.id?(
                      <div style={{marginTop:12,display:'flex',flexDirection:'column',gap:8}}>
                        <input value={signNameInput} onChange={e=>setSignNameInput(e.target.value)} placeholder="Ditt namn (admin-signatur)" style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,padding:'8px 10px',color:T.text,fontSize:13,fontFamily:'inherit'}}/>
                        <div style={{display:'flex',gap:8}}>
                          <button type="button" onClick={()=>{setSigningContractId(null);setSignNameInput('');}} style={{flex:1,padding:'9px',borderRadius:6,border:`1px solid ${T.border}`,background:'transparent',color:T.textSub,fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>Avbryt</button>
                          <button type="button" disabled={!signNameInput.trim()} onClick={async()=>{
                            try{
                              const updated=await api.adminSignMarkOwnerContract(c.id,signNameInput.trim());
                              setAdminMarkContracts(prev=>prev.map(x=>x.id===c.id?updated:x));
                              setSigningContractId(null);setSignNameInput('');
                            }catch(e:any){alert(e.message||'Fel vid signering');}
                          }} style={{flex:2,padding:'9px',borderRadius:6,border:'none',background:signNameInput.trim()?T.amber:'#555',color:'#000',fontSize:13,fontWeight:700,cursor:signNameInput.trim()?'pointer':'not-allowed',fontFamily:'inherit'}}>
                            ✍ Signera och publicera
                          </button>
                        </div>
                      </div>
                    ):(
                      <button type="button" onClick={()=>{setSigningContractId(c.id);setSignNameInput(user?.name??'');}} style={{marginTop:10,width:'100%',padding:'10px',borderRadius:6,border:`1px solid ${T.amber}`,background:'transparent',color:T.amber,fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                        ✍ Signera avtal och publicera mark
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
          <div style={{display:'flex',gap:10,padding:'0 20px 20px'}}>
            <button type="button" onClick={()=>void loadAdminMarkContracts()} style={{flex:1,padding:'11px',borderRadius:8,border:`1px solid ${T.amber}`,background:'transparent',color:T.amber,fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>Uppdatera</button>
            <button type="button" onClick={()=>{setAdminMarkContractsOpen(false);setSigningContractId(null);}} style={{flex:1,padding:'11px',borderRadius:8,border:'none',background:T.amber,color:'#000',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>Klar</button>
          </div>
        </div>
      </div>
    )}

    {/* ── Personalisera navigering modal ── */}
    {showPersonalizeNav&&(()=>{
      // Local editable state for drag-and-drop
      const tabs=ALL_MP_TABS_BASE.map(t=>({...t}));
      const orderedTabs=navTabOrder.length>0?[...tabs].sort((a,b)=>{const ai=navTabOrder.indexOf(a.k);const bi=navTabOrder.indexOf(b.k);return(ai===-1?999:ai)-(bi===-1?999:bi);}):tabs;
      const orderedMarks=markOrderPref.length>0?[...myMarks2].sort((a,b)=>{const ai=markOrderPref.indexOf(a.id);const bi=markOrderPref.indexOf(b.id);return(ai===-1?999:ai)-(bi===-1?999:bi);}):myMarks2;

      const moveTab=(from:number,to:number,list:typeof orderedTabs)=>{
        const arr=[...list];const[item]=arr.splice(from,1);arr.splice(to,0,item);
        const order=arr.map(t=>t.k);setNavTabOrder(order);localStorage.setItem('qvenari_nav_tab_order',JSON.stringify(order));
      };
      const moveMark=(from:number,to:number,list:typeof orderedMarks)=>{
        const arr=[...list];const[item]=arr.splice(from,1);arr.splice(to,0,item);
        const order=arr.map(m=>m.id);setMarkOrderPref(order);localStorage.setItem('qvenari_mark_order',JSON.stringify(order));
      };
      const resetAll=()=>{setNavTabOrder([]);setMarkOrderPref([]);localStorage.removeItem('qvenari_nav_tab_order');localStorage.removeItem('qvenari_mark_order');};

      const rowStyle:React.CSSProperties={display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:T.surface,border:`1px solid ${T.border}`,borderRadius:5,cursor:'grab',userSelect:'none',marginBottom:6,transition:'border-color 0.15s'};
      const gripStyle:React.CSSProperties={color:T.textMuted,fontSize:18,lineHeight:1,flexShrink:0,cursor:'grab'};

      return(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.72)',zIndex:3500,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>e.target===e.currentTarget&&setShowPersonalizeNav(false)}>
          <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,width:'100%',maxWidth:520,maxHeight:'85vh',overflowY:'auto',boxShadow:'0 24px 80px rgba(0,0,0,0.6)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'20px 24px 0'}}>
              <div>
                <div style={{fontSize:18,fontWeight:700,color:T.text}}>Personalisera navigering</div>
                <div style={{fontSize:12,color:T.textSub,marginTop:2}}>Dra och släpp för att ändra ordning</div>
              </div>
              <button onClick={()=>setShowPersonalizeNav(false)} style={{background:'none',border:'none',color:T.textSub,cursor:'pointer',fontSize:22,lineHeight:1,padding:4}}>×</button>
            </div>
            <div style={{padding:'20px 24px 28px'}}>

              {/* Tab order */}
              <div style={{marginBottom:28}}>
                <div style={{fontSize:13,fontWeight:700,color:T.amber,marginBottom:10,textTransform:'uppercase',letterSpacing:'0.08em'}}>Menyordning</div>
                <div>
                  {orderedTabs.map((t,i)=>(
                    <div
                      key={t.k}
                      draggable
                      onDragStart={()=>setDragNavIdx(i)}
                      onDragOver={e=>{e.preventDefault();}}
                      onDrop={e=>{e.preventDefault();if(dragNavIdx!==null&&dragNavIdx!==i){moveTab(dragNavIdx,i,orderedTabs);setDragNavIdx(null);}}}
                      onDragEnd={()=>setDragNavIdx(null)}
                      style={{...rowStyle,opacity:dragNavIdx===i?0.45:1,borderColor:dragNavIdx===i?T.amber:T.border}}
                    >
                      <span style={gripStyle}>⠿</span>
                      <span style={{flex:1,color:T.text,fontSize:14,fontWeight:500}}>{t.l}</span>
                      <span style={{color:T.textMuted,fontSize:11}}>{i+1}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mark order */}
              {orderedMarks.length>0&&(
                <div style={{marginBottom:24}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.amber,marginBottom:10,textTransform:'uppercase',letterSpacing:'0.08em'}}>Mina Jaktmarker</div>
                  <div>
                    {orderedMarks.map((m,i)=>{
                      const img=markImg(m);
                      return(
                        <div
                          key={m.id}
                          draggable
                          onDragStart={()=>setDragMarkIdx(i)}
                          onDragOver={e=>{e.preventDefault();}}
                          onDrop={e=>{e.preventDefault();if(dragMarkIdx!==null&&dragMarkIdx!==i){moveMark(dragMarkIdx,i,orderedMarks);setDragMarkIdx(null);}}}
                          onDragEnd={()=>setDragMarkIdx(null)}
                          style={{...rowStyle,opacity:dragMarkIdx===i?0.45:1,borderColor:dragMarkIdx===i?T.amber:T.border}}
                        >
                          <span style={gripStyle}>⠿</span>
                          <img src={img} alt={m.name} style={{width:36,height:36,objectFit:'cover',borderRadius:3,flexShrink:0}} onError={e=>{(e.target as HTMLImageElement).src=GREEN_PLACEHOLDER}}/>
                          <span style={{flex:1,color:T.text,fontSize:13,fontWeight:500,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{m.name}</span>
                          <span style={{color:T.textMuted,fontSize:11,flexShrink:0}}>{m.municipality||m.county||''}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{display:'flex',gap:10,justifyContent:'flex-end',borderTop:`1px solid ${T.border}`,paddingTop:16}}>
                <button onClick={resetAll} style={{background:'transparent',border:`1px solid ${T.border}`,color:T.textSub,borderRadius:5,padding:'9px 18px',fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>Återställ standard</button>
                <button onClick={()=>setShowPersonalizeNav(false)} style={{background:T.amber,color:'#000',border:'none',borderRadius:5,padding:'9px 22px',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Klar</button>
              </div>
            </div>
          </div>
        </div>
      );
    })()}
  </>);
}
